import { fromJS, List, Map } from 'immutable';
import Actions from 'flow/actions';
import { listToMap } from 'flow/utils';
import { compareChatMessageAge } from 'flow/utils/message-utils';
import { mergeNewChatMessageList } from 'flow/utils/message-utils';

const initialState = Map();

// eslint-disable-next-line complexity
const channelsReducer = (state = initialState, action) => {
    switch (action ? action.type : null) {
        case Actions.CLEAR_STATE:
            return initialState;
        case Actions.RESULT_WIDGET_CHANNEL: {
            const newChannel = fromJS(action.data).update(
                'chatSessionDTOs',
                Map(),
                (sessionList) => listToMap(sessionList, (e) => e.get('key')),
            );

            return state
                .updateIn(
                    ['channels', action.channelKey],
                    Map(),
                    (oldChannel) => {
                        return oldChannel.mergeDeep(newChannel);
                    },
                )
                .setIn(
                    ['channels', action.channelKey, 'chatSessionDTOs'],
                    newChannel.get('chatSessionDTOs'),
                );
        }
        case Actions.RESULT_WIDGET_CHANNELS: {
            const channels = listToMap(fromJS(action.data), (channel) =>
                channel.get('key'),
            );
            const convertSessionListToMap = channels.map((channel) => {
                return channel.update('chatSessionDTOs', Map(), (sessions) =>
                    listToMap(sessions, (s) => s.get('key')),
                );
            });

            return state.update('channels', (prevChannels = Map()) =>
                prevChannels.mergeDeep(convertSessionListToMap),
            );
        }
        case Actions.RESULT_PAGINATED_ARCHIVED_CHANNEL_SESSIONS: {
            return state.updateIn(
                ['channels', action.key, 'chatSessionDTOs'],
                (prevSessions = Map()) =>
                    prevSessions.mergeDeep(
                        listToMap(fromJS(action.data), (session) =>
                            session.get('key'),
                        ),
                    ),
            );
        }
        case Actions.RESULT_CHANNEL_STAT_SUMMARY: {
            return state.setIn(
                ['channels', action.key, 'statistics'],
                fromJS(action.data),
            );
        }
        case Actions.RESULT_WIDGET_MESSAGES: {
            const newMessages = fromJS(action.data);
            if (!state.getIn(['widgetMessages', action.key])) {
                return state.setIn(['widgetMessages', action.key], newMessages);
            }
            return state.updateIn(
                ['widgetMessages', action.key],
                List(),
                (prevMessages) => {
                    return mergeNewChatMessageList(prevMessages, newMessages);
                },
            );
        }
        case Actions.RESULT_CHANNEL_MEMBERS: {
            return state.setIn(
                ['channels', action.channelKey, 'channelMemberDTOs'],
                fromJS(action.data),
            );
        }
        case Actions.RESULT_CHANNEL_ACTIVE_PROFILE: {
            return state.setIn(
                ['channels', action.channelKey, 'activeProfile'],
                fromJS(action.data),
            );
        }
        case Actions.RESULT_CHANNEL_UPDATED_PROFILE: {
            return state.updateIn(
                ['channels', action.channelKey, 'profiles'],
                List(),
                (profiles) => {
                    return profiles.set(
                        profiles.findIndex(
                            (p) => p.get('key') === action.profileKey,
                        ),
                        fromJS(action.data),
                    );
                },
            );
        }
        case Actions.RESULT_CHANNEL_UPDATED_PROFILES: {
            return state.updateIn(
                ['channels', action.channelKey, 'profiles'],
                List(),
                (profiles) => {
                    const { data: profileRespData } = action;

                    return profiles.map((profile) =>
                        profileRespData.some(
                            (prof) => prof.key === profile.get('key'),
                        )
                            ? fromJS(
                                  profileRespData.find(
                                      (profRes) =>
                                          profRes.key === profile.get('key'),
                                  ),
                              )
                            : profile,
                    );
                },
            );
        }
        case Actions.RESULT_UPDATE_CHANNEL_MEMBER: {
            const members = state.getIn([
                'channels',
                action.channelKey,
                'channelMemberDTOs',
            ]);
            const filteredMembers = members.filter(
                (member) => member.get('key') !== action.data.key,
            );
            return state.setIn(
                ['channels', action.channelKey, 'channelMemberDTOs'],
                filteredMembers.push(fromJS(action.data)),
            );
        }
        case Actions.RESULT_WIDGET_MESSAGE: {
            const chatMessage = fromJS(action.data);

            const addOrUpdate = (messages) => {
                const index = messages.findIndex(
                    (m) => m.get('key') === chatMessage.get('key'),
                );

                if (index === -1) {
                    return messages.push(chatMessage);
                } else {
                    return messages.update(index, (prev) =>
                        prev.mergeDeep(chatMessage),
                    );
                }
            };

            const updateSession = (session) => {
                const key = session.get('key')
                    ? session.get('key')
                    : action.key;
                return session
                    .set('key', key)
                    .set('latestMessage', chatMessage)
                    .update('numberOfUnreadMessages', 0, (count) => {
                        return count + (chatMessage.get('read', false) ? 0 : 1);
                    });
            };

            return state
                .updateIn(['widgetMessages', action.key], List(), addOrUpdate)
                .updateIn(
                    [
                        'channels',
                        action.channelKey,
                        'chatSessionDTOs',
                        action.key,
                    ],
                    Map(),
                    updateSession,
                );
        }

        case Actions.CHANNEL_COMPOSER_UPDATE:
            return state.setIn(
                ['composer', action.key, 'text'],
                action.message,
            );

        case Actions.RESULT_OLDER_WIDGET_MESSAGES: {
            const firstMessageKey = state
                .getIn(['widgetMessages', action.key], List())
                .first()
                .get('key');
            return state.updateIn(
                ['widgetMessages', action.key],
                List(),
                (session) => {
                    return fromJS(action.data)
                        .takeUntil(
                            (message) => message.get('key') === firstMessageKey,
                        )
                        .concat(session);
                },
            );
        }
        case Actions.RESET_NUMBER_OF_UNREAD_MESSAGES: {
            return state.setIn(
                [
                    'channels',
                    action.channelKey,
                    'chatSessionDTOs',
                    action.key,
                    'numberOfUnreadMessages',
                ],
                0,
            );
        }
        case Actions.RESULT_WIDGET_MESSAGE_READ: {
            const readMessage = fromJS(action.data);

            const updateMessages = (messages) =>
                messages.map((m) => {
                    const compare = compareChatMessageAge(readMessage, m);
                    const hasBeenRead = compare >= 0;

                    return m.set('read', hasBeenRead);
                });

            const chatSessionDTOs = state.getIn(
                ['channels', action.channelKey, 'chatSessionDTOs'],
                Map(),
            );
            const totalNumberOfUnreadMessagesForChannel =
                chatSessionDTOs.reduce((total, sessionDTO) => {
                    if (
                        sessionDTO.get('key') === action.key ||
                        sessionDTO.get('chatMuted') ||
                        sessionDTO.get('chatArchived')
                    ) {
                        /**
                         * If the chat currently iterated over is the chat just read or if the chat is muted,
                         * we don't want to increment the channel's number of unread messages with that of the read/muted chat
                         */
                        return total;
                    }

                    return total + sessionDTO.get('numberOfUnreadMessages', 0);
                }, 0);

            return state
                .updateIn(
                    ['widgetMessages', action.key],
                    List(),
                    updateMessages,
                )
                .setIn(
                    [
                        'channels',
                        action.channelKey,
                        'chatSessionDTOs',
                        action.key,
                        'numberOfUnreadMessages',
                    ],
                    0,
                )
                .setIn(
                    ['channels', action.channelKey, 'numberOfUnreadMessages'],
                    totalNumberOfUnreadMessagesForChannel,
                );
        }
        case Actions.RESULT_TYPING: {
            const updateTypers = (typers) => {
                if (action.isTyping) {
                    return typers.includes(action.extensionKey)
                        ? typers
                        : typers.push(action.extensionKey);
                } else {
                    return typers.filter(
                        (extensionKey) => extensionKey !== action.extensionKey,
                    );
                }
            };

            const session = state.getIn([
                'channels',
                action.channelKey,
                'chatSessionDTOs',
                action.sessionKey,
            ]);

            if (session) {
                const updatedSession = session.update(
                    'typingUserKeys',
                    List(),
                    updateTypers,
                );
                return state.setIn(
                    [
                        'channels',
                        action.channelKey,
                        'chatSessionDTOs',
                        action.sessionKey,
                    ],
                    updatedSession,
                );
            }

            return state;
        }
        case Actions.RESULT_CHANNEL_CALENDAR: {
            return state.updateIn(['channels', action.key, 'calendar'], () =>
                fromJS(action.data),
            );
        }
        case Actions.RESULT_CHANNEL_OPENING_HOURS: {
            return state.setIn(
                ['channels', action.key, 'calendar', 'openingHours'],
                fromJS(action.data),
            );
        }
        case Actions.RESULT_CHANNEL_ADD_CALENDAR_EVENT: {
            return state.updateIn(
                ['channels', action.key, 'calendar', 'events'],
                List(),
                (events) => events.push(fromJS(action.data)),
            );
        }
        case Actions.RESULT_CHANNEL_UPDATE_CALENDAR_EVENT: {
            return state.updateIn(
                ['channels', action.key, 'calendar', 'events'],
                List(),
                (events) => {
                    return events.set(
                        events.findIndex(
                            (e) =>
                                e.get('key') ===
                                action.originalCalendarEventKey,
                        ),
                        fromJS(action.data),
                    );
                },
            );
        }
        case Actions.RESULT_CHANNEL_REMOVE_CALENDAR_EVENT: {
            return state.updateIn(
                ['channels', action.key, 'calendar', 'events'],
                (events) => {
                    return events.filter(
                        (e) => e.get('key') !== action.data.key,
                    );
                },
            );
        }
        case Actions.RESULT_ARCHIVE_CHANNEL_SESSION: {
            const channelKey = action.key;
            const session = fromJS(action.data);

            return state
                .updateIn(
                    ['channels', channelKey, 'chatSessionDTOs'],
                    Map(),
                    (sessions) => sessions.set(session.get('key'), session),
                )
                .updateIn(
                    ['channels', channelKey, 'numberOfUnreadMessages'],
                    0,
                    (total) => {
                        return total - session.get('numberOfUnreadMessages', 0);
                    },
                );
        }
        case Actions.RESULT_UNARCHIVE_CHANNEL_SESSION: {
            const channelKey = action.key;
            const session = fromJS(action.data);

            return state
                .updateIn(
                    ['channels', channelKey, 'chatSessionDTOs'],
                    Map(),
                    (sessions) => sessions.set(session.get('key'), session),
                )
                .updateIn(
                    ['channels', channelKey, 'numberOfUnreadMessages'],
                    0,
                    (total) => {
                        return total + session.get('numberOfUnreadMessages', 0);
                    },
                );
        }
        case Actions.RESULT_CHANNEL_SESSION: {
            const channelKey = action.key;
            const session = fromJS(action.data);

            return state.updateIn(
                ['channels', channelKey, 'chatSessionDTOs'],
                Map(),
                (sessions) => sessions.set(session.get('key'), session),
            );
        }
        case Actions.RESULT_CHANNEL_SESSION_BADGE_COUNT: {
            const channelKey = action.key;
            return state.updateIn(
                ['channels', channelKey, 'numberOfUnreadMessages'],
                0,
                (count) => ++count,
            );
        }
        default:
            return state;
    }
};

export default channelsReducer;
